// client/src/components/company/CompanyNotifications.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CompanyNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Add timestamp to prevent caching issues
        const res = await axios.get(`/api/notifications?t=${Date.now()}`);
        
        // Validate response format
        if (res.data && Array.isArray(res.data.notifications)) {
          setNotifications(res.data.notifications);
        } else {
          console.error('Unexpected response format:', res.data);
          setError(t('common.errors.unexpectedFormat'));
        }
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`${t('common.errors.fetchNotifications')}: ${err.response?.data?.message || err.message}`);
        setLoading(false);
      }
    };
    
    fetchNotifications();
    
    // Set up polling interval
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [t]);
  
  const markAllAsRead = async () => {
    try {
      const res = await axios.put('/api/notifications/read-all');
      
      if (res.status === 200) {
        setNotifications(notifications.map(notification => ({
          ...notification,
          isRead: true
        })));
      } else {
        console.error('Unexpected response:', res);
        setError(t('common.errors.markAsRead') + ': Unexpected response');
      }
    } catch (err) {
      console.error('Error marking all as read:', err);
      setError(`${t('common.errors.markAsRead')}: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleNotificationClick = async (notification) => {
    try {
      // Mark the notification as read
      await axios.put(`/api/notifications/${notification._id}/read`);
      
      // Update UI to mark as read
      setNotifications(notifications.map(n => 
        n._id === notification._id ? { ...n, isRead: true } : n
      ));
      
      // Navigate based on notification type
      if (notification.relatedEntity && notification.relatedEntity.type) {
        if (notification.relatedEntity.type === 'request' && notification.relatedEntity.id) {
          navigate(`/company/requests/${notification.relatedEntity.id}`);
        } else if (notification.relatedEntity.type === 'bid') {
          // For both bid received and cancelled notifications, navigate to past requests
          navigate('/company/past-requests');
        } else {
          console.log('Unknown notification type or missing ID:', notification);
        }
      } else {
        console.log('Notification missing relatedEntity data:', notification);
      }
    } catch (err) {
      console.error('Error handling notification click:', err);
      setError(`${t('common.errors.handleNotification')}: ${err.response?.data?.message || err.message}`);
    }
  };
  
  // Function to format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return t('common.invalidDate');
      }
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting date:', err);
      return t('common.invalidDate');
    }
  };
  
  // Function to format time for display
  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleTimeString(undefined, { 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch (err) {
      console.error('Error formatting time:', err);
      return '';
    }
  };

  // Function to translate notification title and message
  const translateNotification = (notification) => {
    // Se il titolo è già una chiave di traduzione, usala
    if (notification.titleKey) {
      return {
        title: t(`notifications.types.${notification.titleKey}`),
        message: t(`notifications.messages.${notification.messageKey}`, notification.data || {})
      };
    }
    
    // Altrimenti, prova a inferire dalla stringa esistente (backward compatibility)
    let titleKey = '';
    let messageKey = '';
    let data = {};
    
    // Mappa i titoli esistenti alle chiavi di traduzione
    const titleMapping = {
      'New Bid Received': 'new_bid_received',
      'Bid Cancelled': 'bid_cancelled', 
      'Bid Approved': 'bid_approved',
      'Bid Rejected': 'bid_rejected',
      'New Liquidity Request': 'new_request',
      'Request Completed': 'request_completed',
      'All Transfers Completed': 'all_transfers_completed',
      'Bid Rejected - Request Cancelled': 'bid_rejected_cancelled',
      'Account Approved': 'account_approved'
    };
    
    titleKey = titleMapping[notification.title] || 'noTitle';
    messageKey = titleMapping[notification.title] || 'noMessage';
    
    // Estrai dati dal messaggio per l'interpolazione
    if (notification.message) {
      // Esempi di regex per estrarre dati dinamici dal messaggio
      const bankNameMatch = notification.message.match(/([A-Za-z\s]+)\s+submitted a bid|([A-Za-z\s]+)\s+has cancelled/);
      const requestIdMatch = notification.message.match(/#(\w+)/);
      const amountMatch = notification.message.match(/€([\d,]+)/);
      const rateMatch = notification.message.match(/at\s+([\d.]+%|Euribor\s+\+\s+[\d.]+%)/);
      
      data = {
        bankName: bankNameMatch ? (bankNameMatch[1] || bankNameMatch[2]) : '',
        requestId: requestIdMatch ? requestIdMatch[1] : '',
        amount: amountMatch ? amountMatch[1] : '',
        rate: rateMatch ? rateMatch[1] : ''
      };
    }
    
    return {
      title: t(`notifications.types.${titleKey}`) || notification.title || t('notifications.noTitle'),
      message: t(`notifications.messages.${messageKey}`, data) || notification.message || t('notifications.noMessage')
    };
  };

  // Function to translate notification type display
  const getNotificationTypeDisplay = (relatedEntity) => {
    if (!relatedEntity || !relatedEntity.type) return '';
    
    switch (relatedEntity.type) {
      case 'request':
        return t('requests.request');
      case 'bid':
        return t('bids.bid');
      default:
        return relatedEntity.type;
    }
  };
  
  // Group notifications by date
  const groupNotificationsByDate = () => {
    const groups = {};
    
    notifications.forEach(notification => {
      if (!notification.createdAt) return;
      
      const date = new Date(notification.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
    });
    
    // Convert to array and sort by date (newest first)
    return Object.entries(groups)
      .map(([date, items]) => ({ date, items }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };
  
  const notificationGroups = groupNotificationsByDate();
  
  // Custom styles to override the existing CSS
  const customStyles = {
    notificationItem: {
      padding: '15px',
      borderBottom: '1px solid var(--border)',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      backgroundColor: 'white'
    },
    unreadItem: {
      backgroundColor: 'var(--primary-light)',
      borderLeft: '4px solid var(--primary)'
    },
    notificationHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '6px'
    },
    notificationTitle: {
      fontWeight: '600',
      fontSize: '16px',
      color: 'var(--dark)'
    },
    notificationDate: {
      fontSize: '12px',
      color: 'var(--grey)'
    },
    notificationMessage: {
      margin: '0',
      color: 'var(--grey)',
      lineHeight: '1.5'
    },
    dateHeader: {
      padding: '10px 15px',
      backgroundColor: 'var(--light-grey)',
      fontWeight: '500',
      color: 'var(--dark)',
      borderBottom: '1px solid var(--border)'
    },
    notificationType: {
      fontSize: '12px',
      color: 'var(--grey)',
      marginTop: '8px',
      padding: '2px 6px',
      backgroundColor: 'var(--light-grey)',
      borderRadius: '4px',
      display: 'inline-block'
    },
    cancelledItem: {
      backgroundColor: '#FFF4F4',
      borderLeft: '4px solid #FF6B6B'
    }
  };
  
  // Helper to get notification style based on its type
  const getNotificationStyle = (notification) => {
    if (!notification.isRead) {
      return customStyles.unreadItem;
    }
    
    // Add visual indicator for cancelled bids
    if (notification.type === 'bid_cancelled') {
      return customStyles.cancelledItem;
    }
    
    return {};
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingNotifications')}</div>;
  }
  
  return (
    <div className="company-notifications company-dashboard">
            
      <div className="card company-dashboard">
        <div className="card-title">
          <span>{t('notifications.title')}</span>
          {notifications.some(n => !n.isRead) && (
            <button 
              className="btn btn-light btn-sm"
              onClick={markAllAsRead}
            >
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>
        
        {error && <div className="alert-danger">{error}</div>}
        
        <div>
          {notificationGroups.length > 0 ? (
            notificationGroups.map((group, groupIndex) => (
              <div key={group.date}>
                <div style={customStyles.dateHeader}>
                  {formatDate(group.date)}
                </div>
                {group.items.map((notification) => {
                  const translatedNotification = translateNotification(notification);
                  
                  return (
                    <div 
                      key={notification._id || `notification-${groupIndex}-${Math.random()}`} 
                      style={{
                        ...customStyles.notificationItem,
                        ...getNotificationStyle(notification)
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div style={customStyles.notificationHeader}>
                        <span style={customStyles.notificationTitle}>
                          {translatedNotification.title}
                        </span>
                        <span style={customStyles.notificationDate}>
                          {formatTime(notification.createdAt)}
                        </span>
                      </div>
                      <p style={customStyles.notificationMessage}>
                        {translatedNotification.message}
                      </p>
                      {notification.relatedEntity && (
                        <div style={customStyles.notificationType}>
                          {getNotificationTypeDisplay(notification.relatedEntity)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--grey)' }}>
              <p>{t('notifications.noNotifications')}</p>
            </div>
          )}
          
          {/* Add a styled "View All" button that matches your design system */}
          {notificationGroups.length > 3 && (
            <div style={{ textAlign: 'center', padding: '15px', borderTop: '1px solid var(--border)' }}>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/company/notifications')}
              >
                {t('notifications.viewAll')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyNotifications;